import { memo, useMemo } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Col,
  Combobox,
  type HookFormMethods,
  HookFormResolver,
  PageTitle,
  Row
} from '@dec/shared';
import { File } from 'app/components/form';
import { GridContainer, OnlyGrid } from 'app/components/grid/gridApplication';
import { ConfirmDialog } from 'app/core/dialog';
import { useSpinnerActions } from 'app/layouts/store';
import { catalogs } from 'app/modules/Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from 'app/modules/Parameters/Catalog/Catalog/services/useCatalog';
import { label as labelCommon } from 'app/modules/translation';
import i18next from 'i18next';
import { Bank2 } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useGetDefinitionExcel } from '../hooks/useGetDefinitionExcel';
import { paymentService } from '../services/payment.service';

const validations = yup.object().shape({
  attachment: yup
    .mixed()
    .required(() => i18next.t('common.validations.fieldRequired'))
    .nullable(),

  taxCollector: yup
    .object()
    .nullable()
    .required(() => i18next.t('common.validations.fieldRequired'))
});

interface UploadBankInformationForm {
  attachment: File | null;
  taxCollector: {
    id: string;
    name?: string;
  } | null;
}

export const UploadBankInformationPage = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSpinner, hideSpinner } = useSpinnerActions();

  const {
    data: attachmentDef,
    loading: loadingAttachmentDef,
    error: attachmentDefError
  } = useGetDefinitionExcel(true);

  const [{ data: taxCollectors, loading: loadingTaxCollector, error: taxCollectorError }] =
    useCatalogList(catalogs.taxCollector);

  const colDefs = useMemo(
    () => [
      {
        headerName: t('common.label.header'),
        field: 'header'
      },
      {
        headerName: t('common.label.type'),
        cellRenderer: (params: { data: { type: string } }) => t(`common.label.${params.data.type}`)
      },
      {
        headerName: t('common.label.description'),
        field: 'description'
      }
    ],
    [t]
  );

  const onSubmit = async (data: UploadBankInformationForm) => {
    const confirmed = await ConfirmDialog({
      titleModal: t('common.dialog.warning-title'),
      message: t('common.messages.confirm'),
      okButton: labelCommon('ok'),
      cancelButton: labelCommon('cancel')
    });

    if (!confirmed || !data.taxCollector || !data.attachment) {
      return;
    }

    try {
      showSpinner();

      const response = await paymentService.uploadPayments(
        paymentService.createPaymentFileUploadRequest(data.attachment, data.taxCollector.id)
      );

      navigate(paymentService.paymentFileUploadSuccessPath(response));
    } finally {
      hideSpinner();
    }
  };

  const loading =
    loadingAttachmentDef ||
    Boolean(attachmentDefError) ||
    loadingTaxCollector ||
    Boolean(taxCollectorError);

  if (loading) {
    return null;
  }

  return (
    <>
      <PageTitle title={t('taxpayer-payments.title.pageUploadPayments')} icon={Bank2} />

      <Row>
        <Col>
          <h2 className="bg-white">{t('taxpayer-payments.label.excelDefinition')}</h2>
        </Col>
      </Row>

      <Row>
        <Col>
          <GridContainer autoHeight>
            <OnlyGrid domLayout="autoHeight" columnDefs={colDefs} data={attachmentDef ?? []} />
          </GridContainer>
        </Col>
      </Row>

      <hr />

      <Row>
        <Col>
          <HookFormResolver onSubmit={onSubmit} validations={validations}>
            {({}: HookFormMethods) => (
              <>
                <Row>
                  <Col>
                    <Combobox
                      code="id"
                      display="name"
                      data={taxCollectors}
                      label={t('taxpayer-payments.label.taxCollector')}
                      name="taxCollector"
                    />
                  </Col>

                  <Col>
                    <File
                      name="attachment"
                      label={t('common.button.file')}
                      accept=".xlsx,.xls,.csv"
                      noUpload
                    />
                  </Col>
                </Row>

                <BtnToolbar>
                  <BtnGroup>
                    <Button action={ButtonAction.cancel} onClick={() => navigate('/')} />
                  </BtnGroup>

                  <BtnGroup>
                    <Button
                      action={ButtonAction.save}
                      type="submit"
                      variant="primary"
                      text={t('taxpayer-payments.title.pageUploadPayments')}
                    />
                  </BtnGroup>
                </BtnToolbar>
              </>
            )}
          </HookFormResolver>
        </Col>
      </Row>
    </>
  );
});

UploadBankInformationPage.displayName = 'UploadBankInformationPage';
